import { Component, OnInit, OnDestroy, AfterViewInit, Input, OnChanges, SimpleChanges } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Chart, ChartConfiguration, ChartData, registerables } from "chart.js";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import { DashboardCallCenterService } from "./services/dashboard-call-center.service";
import {
  CallCenterFiltros,
  TipoPeriodoEnum,
  TaxaQualificacaoResult,
  ContatosAtualizadosTemporalResult,
  AproveitamentoListasResult,
  StatusQualificadosResult,
  MensagensPorAtendenteResult,
  ContatosSemMensagemResult,
  TaxaConversaoProjetosResult,
  ResumoPerformanceLista,
  ResumoPerformanceListaResult
} from "./models/dashboard-call-center.model";
import { ToastService } from "src/app/shared/toasts/toast-service";
import { AuthService } from "src/app/core/services/auth/auth.service";
import { DashboardService } from "src/app/pages/dashboard/services/dashboard.service";
import { ListaContato } from "src/app/pages/dashboard/models/dashboard.model";

// Registrar todos os componentes do Chart.js
Chart.register(...registerables);

@Component({
  selector: "app-dashboard-call-center",
  templateUrl: "./dashboard-call-center.component.html",
  styleUrls: ["./dashboard-call-center.component.scss"],
})
export class DashboardCallCenterComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() hideTitle: boolean = false;
  @Input() filtrosExternos?: any; // Filtros vindos do componente pai
  
  breadCrumbItems!: Array<{}>;
  private readonly destroy$ = new Subject<void>();

  // Filtros
  filtroForm!: FormGroup;
  filtros: CallCenterFiltros;

  // Gráficos
  contatosAtualizadosChart: Chart<'line'> | null = null;
  aproveitamentoListasChart: Chart<'bar'> | null = null;
  statusQualificadosChart: Chart<'pie'> | null = null;
  mensagensAtendenteChart: Chart<'bar'> | null = null;
  contatosSemMensagemChart: Chart<'bar'> | null = null;
  taxaConversaoChart: Chart<'doughnut'> | null = null;
  
  temAcessoAoDashboard: boolean = false;
  
  // Estados
  isLoading = false;
  error = '';
  taxaConversaoValor = 0;
  graficosCarregadosInicialmente = false; // Flag para controlar carregamento inicial

  // Resumo de Performance por Lista
  listas: ListaContato[] = [];
  listaIdParaResumo: string | null = null;
  resumoPerformanceLista: ResumoPerformanceLista | null = null;
  carregandoResumo = false;

  // Dados para KPIs
  kpiTaxaQualificacao = 0;
  kpiTaxaConversao = 0;
  kpiTotalContatos = 0;
  kpiTotalQualificados = 0;
  kpiTotalMensagens = 0;
  kpiTotalProjetos = 0;

  // Meses para select
  meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  constructor(
    private dashboardService: DashboardCallCenterService,
    private dashboardMainService: DashboardService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private authService: AuthService
  ) {
    const hoje = new Date();
    this.filtros = {
      tipoPeriodo: TipoPeriodoEnum.Mes,
      mes: hoje.getMonth() + 1,
      ano: hoje.getFullYear()
    };

    this.filtroForm = this.fb.group({
      tipoPeriodo: [TipoPeriodoEnum.Mes],
      mes: [hoje.getMonth() + 1],
      ano: [hoje.getFullYear()],
      dataInicio: [null],
      dataFim: [null],
      feiraId: [null],
      listaId: [null],
      atendente: [null]
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando os filtros externos mudarem, apenas converter
    // O carregamento será feito quando a aba for ativada
    if (changes['filtrosExternos'] && this.filtrosExternos && this.hideTitle) {
      this.converterFiltrosExternos();
      // Não carregar aqui - será carregado quando a aba for ativada
    }
  }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: "Call Center" },
      { label: "Dashboard", active: true },
    ];

    // Carregar listas para o resumo de performance
    this.carregarListas();

    // Se estiver dentro de uma aba, usar filtros externos
    if (this.hideTitle && this.filtrosExternos) {
      this.converterFiltrosExternos();
      // Será carregado no ngAfterViewInit quando os canvas estiverem prontos
    } else {
      // Configurar observação de mudanças nos filtros internos
      this.filtroForm.valueChanges
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.aplicarFiltros();
        });

      // Carregar dados iniciais
      this.aplicarFiltros();
    }
  }

  converterFiltrosExternos(): void {
    if (!this.filtrosExternos) return;
    
    this.filtros = {
      tipoPeriodo: this.filtrosExternos.tipoPeriodo,
      mes: this.filtrosExternos.mes,
      ano: this.filtrosExternos.ano,
      dataInicio: this.filtrosExternos.dataInicio,
      dataFim: this.filtrosExternos.dataFim,
      feiraId: this.filtrosExternos.feiraId,
      listaId: this.filtrosExternos.listaId,
      atendente: this.filtrosExternos.atendente
    };
  }

  ngAfterViewInit(): void {
    // Se não estiver dentro de uma aba, carregar normalmente
    if (!this.hideTitle) {
      setTimeout(() => {
        if (!this.isLoading) {
          const canvasIds = [
            'contatosAtualizadosChart',
            'aproveitamentoListasChart',
            'statusQualificadosChart',
            'mensagensAtendenteChart',
            'contatosSemMensagemChart'
          ];
          
          const allCanvasExist = canvasIds.every(id => document.getElementById(id) !== null);
          
          if (allCanvasExist) {
            this.carregarGraficos();
          }
        }
      }, 100);
    }
    // Se estiver dentro de uma aba, não carregar aqui - será carregado quando a aba for ativada
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destruirGraficos();
  }

  aplicarFiltros(): void {
    const formValue = this.filtroForm.value;
    
    this.filtros = {
      tipoPeriodo: formValue.tipoPeriodo,
      mes: formValue.tipoPeriodo === TipoPeriodoEnum.Mes ? formValue.mes : undefined,
      ano: formValue.ano,
      dataInicio: formValue.tipoPeriodo === TipoPeriodoEnum.Personalizado ? formValue.dataInicio : undefined,
      dataFim: formValue.tipoPeriodo === TipoPeriodoEnum.Personalizado ? formValue.dataFim : undefined,
      feiraId: formValue.feiraId || undefined,
      listaId: formValue.listaId || undefined,
      atendente: formValue.atendente || undefined
    };

    this.carregarGraficos();
  }

  carregarGraficos(): void {
    // Verificar se os canvas existem antes de continuar
    const canvasIds = [
      'contatosAtualizadosChart',
      'aproveitamentoListasChart',
      'statusQualificadosChart',
      'mensagensAtendenteChart',
      'contatosSemMensagemChart'
    ];
    
    const allCanvasExist = canvasIds.every(id => document.getElementById(id) !== null);
    
    if (!allCanvasExist && this.hideTitle) {
      // Se está dentro de uma aba e os canvas não existem, aguardar mais um pouco
      setTimeout(() => {
        this.carregarGraficos();
      }, 300);
      return;
    }
    
    this.isLoading = true;
    this.error = '';

    this.dashboardService.consultarTodosGraficos(this.filtros).subscribe({
      next: (result) => {
        this.isLoading = false;
        
        // Atualizar KPIs
        if (result.taxaQualificacao) {
          this.kpiTaxaQualificacao = result.taxaQualificacao.taxaQualificacao;
          this.kpiTotalContatos = result.taxaQualificacao.totalContatos;
        }
        
        if (result.taxaConversaoProjetos) {
          this.kpiTaxaConversao = result.taxaConversaoProjetos.taxaConversao;
          this.kpiTotalQualificados = result.taxaConversaoProjetos.totalQualificados;
        }
        
        if (result.mensagensPorAtendente) {
          this.kpiTotalMensagens = result.mensagensPorAtendente.totalMensagens;
        }
        
        if (result.totalProjetos) {
          this.kpiTotalProjetos = result.totalProjetos.totalProjetos;
        }
        
        // Aguardar um pouco para garantir que os canvas estejam no DOM após o *ngIf
        setTimeout(() => {
          if (result.contatosAtualizadosTemporal) {
            this.criarGraficoContatosAtualizados(result.contatosAtualizadosTemporal);
          }
          
          if (result.aproveitamentoListas) {
            this.criarGraficoAproveitamentoListas(result.aproveitamentoListas);
          }
          
          if (result.statusQualificados) {
            this.criarGraficoStatusQualificados(result.statusQualificados);
          }
          
          // Carregar gráficos que podem não estar no endpoint principal via endpoints individuais
          if (result.mensagensPorAtendente) {
            this.criarGraficoMensagensAtendente(result.mensagensPorAtendente);
          } else {
            this.carregarMensagensPorAtendenteIndividual();
          }
          
          if (result.contatosSemMensagem) {
            this.criarGraficoContatosSemMensagem(result.contatosSemMensagem);
          } else {
            this.carregarContatosSemMensagemIndividual();
          }
          
          // Gráfico de Taxa de Conversão removido
          // Apenas atualizar o KPI se necessário
          if (result.taxaConversaoProjetos) {
            this.kpiTaxaConversao = result.taxaConversaoProjetos.taxaConversao;
            this.kpiTotalQualificados = result.taxaConversaoProjetos.totalQualificados;
          } else {
            this.carregarTaxaConversaoIndividual();
          }
        }, 300);
        
      },
      error: (error) => {
        console.error('Erro ao carregar gráficos do Call Center:', error);
        this.error = 'Erro ao carregar os gráficos. Tente novamente.';
        this.isLoading = false;
        this.toastService.show('Erro ao carregar gráficos do dashboard', {
          classname: 'bg-danger text-light',
        });
      }
    });
  }

  destruirGraficos(): void {
    const charts = [
      this.contatosAtualizadosChart,
      this.aproveitamentoListasChart,
      this.statusQualificadosChart,
      this.mensagensAtendenteChart,
      this.contatosSemMensagemChart
    ];

    charts.forEach(chart => {
      if (chart) {
        chart.destroy();
      }
    });
  }

  // 1. Contatos Atualizados Temporal
  criarGraficoContatosAtualizados(data: ContatosAtualizadosTemporalResult): void {
    const ctx = document.getElementById('contatosAtualizadosChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.contatosAtualizadosChart) {
      this.contatosAtualizadosChart.destroy();
    }

    const chartData: ChartData<'line'> = {
      labels: data.labels,
      datasets: [{
        label: 'Contatos Atualizados',
        data: data.quantidades,
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `Contatos atualizados: ${context.parsed.y}`;
              }
            }
          },
          title: {
            display: true,
            text: `Total: ${data.totalContatos} contatos | Média: ${data.mediaContatos.toFixed(1)} por período`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      }
    };

    try {
      this.contatosAtualizadosChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de contatos atualizados:', error);
    }
  }

  // 3. Aproveitamento por Lista
  criarGraficoAproveitamentoListas(data: AproveitamentoListasResult): void {
    const ctx = document.getElementById('aproveitamentoListasChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.aproveitamentoListasChart) {
      this.aproveitamentoListasChart.destroy();
    }

    // Processar labels
    const labelsProcessados = data.labels.map(label => {
      const parts = label.split('|');
      return parts[0].trim();
    });

    const chartData: ChartData<'bar'> = {
      labels: labelsProcessados,
      datasets: [{
        label: 'Taxa de Qualificação (%)',
        data: data.taxasQualificacao,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
        minBarLength: 20 // Altura mínima de 20px para barras pequenas
      }]
    };

    // Plugin para garantir altura mínima nas barras pequenas
    const minBarHeightPlugin = {
      id: 'minBarHeightPluginAproveitamento',
      afterUpdate: (chart: Chart) => {
        const minHeight = 25; // Altura mínima em pixels
        
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          const yScale = (chart as any).scales.y;
          const base = yScale.getPixelForValue(0);
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            if (value === 0 || value < 0) return;
            
            const y = bar.y;
            const currentHeight = Math.abs(y - base);
            
            // Se a barra for muito pequena, ajustar sua altura visual
            if (currentHeight < minHeight) {
              const direction = y < base ? 1 : -1;
              bar.height = minHeight;
              bar.y = base - (minHeight * direction);
            }
          });
        });
      }
    };

    // Plugin customizado para adicionar rótulos dentro das barras verticais
    const datalabelsPlugin = {
      id: 'datalabelsAproveitamento',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const yScale = (chart as any).scales.y;
        const base = yScale.getPixelForValue(0);

        meta.data.forEach((bar: any, index: number) => {
          if (!bar || index >= data.taxasQualificacao.length) return;
          
          const value = data.taxasQualificacao[index];
          const x = bar.x;
          const y = bar.y;
          const height = Math.abs(y - base);
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Para barras verticais, o y é o topo, então y - height/2 é o centro
          const labelY = y < base ? y + height / 2 : y - height / 2;
          
          // Só mostrar rótulo se a barra tiver altura suficiente
          if (height > 15) {
            ctx.fillText(value.toFixed(1) + '%', x, labelY);
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            position: 'nearest',
            yAlign: 'bottom',
            xAlign: 'center',
            callbacks: {
              label: (context) => {
                const index = context.dataIndex;
                return [
                  `Taxa: ${data.taxasQualificacao[index].toFixed(1)}%`,
                  `Qualificados: ${data.totalQualificados[index]}`,
                  `Total: ${data.totalContatos[index]}`
                ];
              }
            }
          },
          title: {
            display: true,
            text: 'Taxa de Aproveitamento por Lista'
          }
        },
        scales: {
          x: {
            beginAtZero: true
          },
          y: {
            beginAtZero: true,
            max: 100,
            min: -0.5, // Permite que barras pequenas tenham altura mínima visual
            ticks: {
              callback: (value: any) => {
                // Não exibir valores negativos no eixo Y
                if (value < 0) return '';
                return value + '%';
              },
              stepSize: 10
            }
          }
        }
      },
      plugins: [minBarHeightPlugin, datalabelsPlugin]
    };

    try {
      this.aproveitamentoListasChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de aproveitamento de listas:', error);
    }
  }

  // 4. Status Qualificados
  criarGraficoStatusQualificados(data: StatusQualificadosResult): void {
    const ctx = document.getElementById('statusQualificadosChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.statusQualificadosChart) {
      this.statusQualificadosChart.destroy();
    }

    // Processar labels e extrair cores do formato badge
    const labelsProcessados: string[] = [];
    const coresExtraidas: string[] = [];

    data.labels.forEach(label => {
      const parts = label.split('|');
      labelsProcessados.push(parts[0].trim());
      
      // Tentar extrair cor do formato badge (ex: "bg-success-subtle")
      if (parts.length > 1) {
        const corBadge = parts[1].trim();
        const corRgba = this.converterBadgeParaRgba(corBadge);
        coresExtraidas.push(corRgba);
      }
    });

    // Cores padrão
    const coresPadrao = [
      'rgba(75, 192, 192, 0.8)',   // Verde (success)
      'rgba(255, 206, 86, 0.8)',   // Amarelo (warning)
      'rgba(255, 99, 132, 0.8)',   // Vermelho (danger)
      'rgba(153, 102, 255, 0.8)',  // Roxo (info)
      'rgba(255, 159, 64, 0.8)',   // Laranja
      'rgba(54, 162, 235, 0.8)',   // Azul (primary)
      'rgba(201, 203, 207, 0.8)'   // Cinza (secondary)
    ];

    // Usar cores extraídas se disponíveis, senão usar cores da API, senão usar padrão
    let coresFinais: string[];
    if (coresExtraidas.length === labelsProcessados.length && coresExtraidas.every(c => c !== '')) {
      coresFinais = coresExtraidas;
    } else if (data.cores && data.cores.length > 0 && data.cores.every(c => c && c.startsWith('rgba'))) {
      coresFinais = data.cores;
    } else {
      coresFinais = coresPadrao.slice(0, labelsProcessados.length);
    }

    const chartData: ChartData<'pie'> = {
      labels: labelsProcessados,
      datasets: [{
        data: data.quantidades,
        backgroundColor: coresFinais,
        borderColor: '#fff',
        borderWidth: 2
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das fatias do gráfico de pizza
    const datalabelsPlugin = {
      id: 'datalabelsStatusQualificados',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        const chartArea = chart.chartArea;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const radius = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) / 2;
        
        ctx.save();
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((arc: any, index: number) => {
          if (!arc || index >= data.quantidades.length) return;
          
          const value = data.quantidades[index];
          const total = data.totalQualificados;
          const percentual = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          // Calcular o ângulo médio da fatia
          const startAngle = arc.startAngle;
          const endAngle = arc.endAngle;
          const angle = (startAngle + endAngle) / 2;
          
          // Calcular posição do rótulo (no meio da fatia, a 60% do raio)
          const labelRadius = radius * 0.6;
          const labelX = centerX + Math.cos(angle) * labelRadius;
          const labelY = centerY + Math.sin(angle) * labelRadius;
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só mostrar rótulo se a fatia tiver tamanho suficiente (mais de 5% do total)
          const slicePercent = (value / total) * 100;
          
          if (slicePercent > 5) {
            // Mostrar valor e percentual
            ctx.fillText(`${value}`, labelX, labelY - 8);
            ctx.font = '12px Arial';
            ctx.fillText(`(${percentual}%)`, labelX, labelY + 8);
            ctx.font = 'bold 14px Arial'; // Restaurar fonte
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'pie'> = {
      type: 'pie',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = data.totalQualificados;
                const percentual = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${label}: ${value} (${percentual}%)`;
              }
            }
          },
          title: {
            display: true,
            text: `Total de Qualificados: ${data.totalQualificados}`
          }
        }
      },
      plugins: [datalabelsPlugin]
    };

    try {
      this.statusQualificadosChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de status qualificados:', error);
    }
  }

  // 5. Mensagens por Atendente
  criarGraficoMensagensAtendente(data: MensagensPorAtendenteResult): void {
    const ctx = document.getElementById('mensagensAtendenteChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.mensagensAtendenteChart) {
      this.mensagensAtendenteChart.destroy();
    }

    // Validar dados
    if (!data.labels || !data.quantidades || data.labels.length === 0 || data.quantidades.length === 0) {
      return;
    }

    const chartData: ChartData<'bar'> = {
      labels: data.labels,
      datasets: [{
        label: 'Mensagens Enviadas',
        data: data.quantidades,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras
    const datalabelsPlugin = {
      id: 'datalabelsMensagens',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((bar: any, index: number) => {
          if (!bar || index >= data.quantidades.length) return;
          
          const value = data.quantidades[index];
          
          // Obter coordenadas da barra
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(y - base);
          
          // Usar cor branca para melhor contraste com o fundo azul
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Para barras verticais, o y é o topo da barra
          // O centro vertical seria: base - height/2 (ou y - height/2 se y < base)
          const labelY = y < base ? y + height / 2 : y - height / 2;
          
          // Só mostrar rótulo se a barra tiver altura suficiente
          if (height > 15) {
            ctx.fillText(value.toString(), x, labelY);
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `Mensagens: ${context.parsed.y}`;
              }
            }
          },
          title: {
            display: true,
            text: `Total: ${data.totalMensagens} mensagens | Média: ${data.mediaMensagens.toFixed(1)} por atendente`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      },
      plugins: [datalabelsPlugin]
    };

    try {
      this.mensagensAtendenteChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de mensagens por atendente:', error);
    }
  }

  // 6. Contatos Sem Mensagem
  criarGraficoContatosSemMensagem(data: ContatosSemMensagemResult): void {
    const ctx = document.getElementById('contatosSemMensagemChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.contatosSemMensagemChart) {
      this.contatosSemMensagemChart.destroy();
    }

    // Validar dados
    if (data.totalContatos === undefined || data.totalComMensagem === undefined || data.totalSemMensagem === undefined) {
      return;
    }

    const valores = [data.totalComMensagem, data.totalSemMensagem];

    const chartData: ChartData<'bar'> = {
      labels: ['Com Mensagem', 'Sem Mensagem'],
      datasets: [{
        label: 'Contatos',
        data: valores,
        backgroundColor: [
          'rgba(75, 192, 192, 0.6)',
          'rgba(255, 99, 132, 0.6)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras
    const datalabelsPlugin = {
      id: 'datalabelsContatosSemMensagem',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((bar: any, index: number) => {
          if (!bar || index >= valores.length) return;
          
          const value = valores[index];
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(y - base);
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Para barras verticais, o y é o topo da barra
          const labelY = y < base ? y + height / 2 : y - height / 2;
          
          // Só mostrar rótulo se a barra tiver altura suficiente
          if (height > 15) {
            ctx.fillText(value.toString(), x, labelY);
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.parsed.y;
                const total = data.totalContatos;
                const percentual = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${context.label}: ${value} (${percentual}%)`;
              }
            }
          },
          title: {
            display: true,
            text: `Pendentes: ${data.totalSemMensagem} (${data.percentualSemMensagem.toFixed(1)}%)`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      },
      plugins: [datalabelsPlugin]
    };

    try {
      this.contatosSemMensagemChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de contatos sem mensagem:', error);
    }
  }

  // 7. Taxa de Conversão
  criarGraficoTaxaConversao(data: TaxaConversaoProjetosResult): void {
    const ctx = document.getElementById('taxaConversaoChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.taxaConversaoChart) {
      this.taxaConversaoChart.destroy();
    }

    // Validar dados
    if (data.totalQualificados === undefined || data.totalComProjeto === undefined || data.totalSemProjeto === undefined) {
      return;
    }

    const valores = [data.totalComProjeto, data.totalSemProjeto];

    const chartData: ChartData<'doughnut'> = {
      labels: ['Convertidos', 'Não Convertidos'],
      datasets: [{
        data: valores,
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',   // Verde-água (mesma cor de "Qualificados")
          'rgba(255, 99, 132, 0.8)'    // Vermelho/rosa (mesma cor de "Não Qualificados")
        ],
        borderWidth: 0
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das fatias do gráfico doughnut
    const datalabelsPlugin = {
      id: 'datalabelsTaxaConversao',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        const chartArea = chart.chartArea;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const radius = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) / 2;
        // Para doughnut, considerar o cutout (75% = 0.75)
        const innerRadius = radius * 0.75;
        const labelRadius = innerRadius + (radius - innerRadius) * 0.5; // Meio do anel
        
        ctx.save();
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((arc: any, index: number) => {
          if (!arc || index >= valores.length) return;
          
          const value = valores[index];
          const total = data.totalQualificados;
          const percentual = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          // Calcular o ângulo médio da fatia
          const startAngle = arc.startAngle;
          const endAngle = arc.endAngle;
          const angle = (startAngle + endAngle) / 2;
          
          // Calcular posição do rótulo (no meio do anel)
          const labelX = centerX + Math.cos(angle) * labelRadius;
          const labelY = centerY + Math.sin(angle) * labelRadius;
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só mostrar rótulo se a fatia tiver tamanho suficiente (mais de 5% do total)
          const slicePercent = (value / total) * 100;
          
          if (slicePercent > 5) {
            // Mostrar valor e percentual
            ctx.fillText(`${value}`, labelX, labelY - 8);
            ctx.font = '12px Arial';
            ctx.fillText(`(${percentual}%)`, labelX, labelY + 8);
            ctx.font = 'bold 14px Arial'; // Restaurar fonte
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          }
        }
      },
      plugins: [datalabelsPlugin]
    };

    try {
      this.taxaConversaoChart = new Chart(ctx, config);
      this.taxaConversaoValor = data.taxaConversao;
    } catch (error) {
      console.error('Erro ao criar gráfico de taxa de conversão:', error);
    }
  }

  getTaxaConversao(): number {
    return this.taxaConversaoValor;
  }

  // Métodos para carregar gráficos via endpoints individuais quando não estão no endpoint principal
  carregarMensagensPorAtendenteIndividual(): void {
    this.dashboardService.consultarMensagensPorAtendente(this.filtros).subscribe({
      next: (data) => {
        this.kpiTotalMensagens = data.totalMensagens;
        setTimeout(() => {
          this.criarGraficoMensagensAtendente(data);
        }, 100);
      },
      error: (error) => {
        console.error('Erro ao carregar mensagens por atendente:', error);
      }
    });
  }

  carregarContatosSemMensagemIndividual(): void {
    this.dashboardService.consultarContatosSemMensagem(this.filtros).subscribe({
      next: (data) => {
        setTimeout(() => {
          this.criarGraficoContatosSemMensagem(data);
        }, 100);
      },
      error: (error) => {
        console.error('Erro ao carregar contatos sem mensagem:', error);
      }
    });
  }

  carregarTaxaConversaoIndividual(): void {
    this.dashboardService.consultarTaxaConversaoProjetos(this.filtros).subscribe({
      next: (data) => {
        this.kpiTaxaConversao = data.taxaConversao;
        this.kpiTotalQualificados = data.totalQualificados;
        // Gráfico de Taxa de Conversão removido - apenas atualizar KPIs
      },
      error: (error) => {
        console.error('Erro ao carregar taxa de conversão:', error);
      }
    });
  }
  

  // Método auxiliar para converter classes CSS badge para rgba
  private converterBadgeParaRgba(badgeClass: string): string {
    if (!badgeClass) return 'rgba(201, 203, 207, 0.8)'; // Cinza padrão

    const badgeLower = badgeClass.toLowerCase().trim();
    
    // Mapear classes Bootstrap para rgba
    if (badgeLower.includes('success') || badgeLower.includes('verde')) {
      return 'rgba(75, 192, 192, 0.8)'; // Verde
    }
    if (badgeLower.includes('warning') || badgeLower.includes('amarelo')) {
      return 'rgba(255, 206, 86, 0.8)'; // Amarelo
    }
    if (badgeLower.includes('danger') || badgeLower.includes('vermelho')) {
      return 'rgba(255, 99, 132, 0.8)'; // Vermelho
    }
    if (badgeLower.includes('info') || badgeLower.includes('azul')) {
      return 'rgba(54, 162, 235, 0.8)'; // Azul
    }
    if (badgeLower.includes('primary')) {
      return 'rgba(54, 162, 235, 0.8)'; // Azul primário
    }
    if (badgeLower.includes('secondary') || badgeLower.includes('cinza')) {
      return 'rgba(201, 203, 207, 0.8)'; // Cinza
    }
    
    // Se já for rgba, retornar como está
    if (badgeClass.startsWith('rgba')) {
      return badgeClass;
    }
    
    // Padrão: cinza
    return 'rgba(201, 203, 207, 0.8)';
  }

  verificarPermissoes(): void {
    // Verifica se o usuário tem permissão de Editar OU Ver em Adm_Usuarios
    this.temAcessoAoDashboard = 
      this.authService.verificarPermissao('Adm_Usuarios', 'Editar') &&
      this.authService.verificarPermissao('Adm_Usuarios', 'Ver');
    
   
  }

  // Métodos para Resumo de Performance por Lista
  carregarListas(): void {
    this.dashboardMainService.consultarListasDeContatosGerados().subscribe({
      next: (response) => {
        this.listas = response.listas || [];
        
        // Selecionar automaticamente a primeira lista se houver listas e nenhuma estiver selecionada
        if (this.listas.length > 0 && !this.listaIdParaResumo) {
          this.listaIdParaResumo = this.listas[0].id;
          // Carregar o resumo da primeira lista automaticamente
          this.carregarResumoPerformanceLista();
        }
      },
      error: (error) => {
        console.error('Erro ao carregar listas:', error);
        this.listas = [];
      }
    });
  }

  carregarResumoPerformanceLista(): void {
    if (!this.listaIdParaResumo) {
      this.resumoPerformanceLista = null;
      return;
    }
    
    this.carregandoResumo = true;
    
    // Usar filtros padrão (mês e ano atual) independentemente dos filtros do formulário
    const hoje = new Date();
    const filtros = {
      tipoPeriodo: TipoPeriodoEnum.Mes,
      mes: hoje.getMonth() + 1,
      ano: hoje.getFullYear(),
      dataInicio: undefined,
      dataFim: undefined,
      feiraId: undefined,
      listaId: this.listaIdParaResumo,
      atendente: undefined
    };
    
    this.dashboardService.consultarResumoPerformanceLista(filtros).subscribe({
      next: (result) => {
        this.resumoPerformanceLista = result.resumo;
        this.carregandoResumo = false;
      },
      error: (error) => {
        console.error('Erro ao carregar resumo de performance da lista:', error);
        this.resumoPerformanceLista = null;
        this.carregandoResumo = false;
        this.toastService.show('Erro ao carregar resumo da lista', {
          classname: 'bg-danger text-light',
        });
      }
    });
  }

  formatarPercentual(valor: number): string {
    return `${valor.toFixed(1)}%`;
  }
  
  formatarDecimal(valor: number): string {
    return valor.toFixed(2);
  }
}

